import { Middleware } from "@reduxjs/toolkit";
import { alert } from "../../../utils/APICalls/APICalls";
import {
  DEFAULT_RETRY,
  NETWORK_ERROR_RETRY,
  isNetworkErroFunc,
} from "../../../utils/calculateCartHepler";
import { getCookie } from "../../../utils/CookieHelper";
import { COOKIE_ENUM } from "../../../utils/enums";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import {
  FranchiseDeliveryConfig,
  PAGER_DUTY_ERROR_URGENCY,
} from "../../../utils/types";
import { AppDispatch, RootState } from "../app/store";
import { calculateCartWithOffersCall } from "../http/cartAndLoyaltyV2";

let pendingRemovalOperation = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const cartMiddleware: Middleware =
  (store) => (next) => (action: any) => {
    if (action.type === "cartLoyaltyV2/removeItemAction") {
      if (pendingRemovalOperation) {
        console.log(
          "Blocking duplicate removal action, operation already in progress",
        );

        return;
      }

      pendingRemovalOperation = true;

      // Set a timeout to reset the flag
      setTimeout(() => {
        pendingRemovalOperation = false;
      }, 100); // Adjust this timeout as needed
    }

    // Check if the action is one of the cart actions
    const cartActions = [
      "cartLoyaltyV2/addItemAction",
      "cartLoyaltyV2/removeItemAction",
      "cartLoyaltyV2/editItemAction",
      "cartLoyaltyV2/deliveryFeesAction",
      "cartLoyaltyV2/resetCartAction",
      "cartLoyaltyV2/redeemOfferAction",
      "cartLoyaltyV2/removeOfferAction",
      "cartLoyaltyV2/receiptErrorAction",
      "queryParams/setQueryParam",
      "delivery/setCurrentDeliveryConfig",
      "delivery/setDeliveryMinMaxFees",
    ];

    const oldState = store.getState() as RootState;

    const result = next(action);

    if (action.type === "delivery/setCurrentDeliveryConfig") {
      const currentDeliveryFees = oldState.delivery.deliveryFees;
      const newDeliveryFees = (action.payload as FranchiseDeliveryConfig | null)
        ?.deliveryFees;

      // Skip calculation if the delivery config hasn't actually changed
      if (currentDeliveryFees === newDeliveryFees) {
        return result;
      }
    }

    // ADDING RETRY LOGIC
    // Check if this is a rejected calculateCartWithOffersCall action
    if (action.type === "calculateCartWithOffersCall/rejected") {
      const error = action.error;
      const meta = action.meta;
      const currentRetryCount = meta?.arg?.retryCount || 0;

      // Parse error message to determine type
      const errMsg = JSON.stringify(error?.message || "");
      const isNetworkError = isNetworkErroFunc(errMsg);

      // Get the current state to access cart actions
      const state = store.getState() as RootState;
      // Handle network errors
      if (isNetworkError) {
        if (currentRetryCount < NETWORK_ERROR_RETRY) {
          const errorEnum =
            currentRetryCount < DEFAULT_RETRY
              ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
              : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

          alert(
            `${errorEnum}`,
            `Error: ${errMsg} , Cart Actions: ${JSON.stringify(
              state.cartLoyaltyV2Reducer.cartActions,
            )}`,
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError,
          );
          (store.dispatch as AppDispatch)(
            calculateCartWithOffersCall({ retryCount: currentRetryCount + 1 }),
          );
        } else {
          // Max network retries reached, show alert
          alert(
            `${AlertErrorType.Error_CalculateCartCall_After_3_FailedNetworkCalls}`,
            `Error: ${errMsg} , Cart Actions: ${JSON.stringify(
              state.cartLoyaltyV2Reducer.cartActions,
            )}`,
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError,
          );
        }
      } else {
        // Non-network errors
        const errorEnum =
          currentRetryCount < DEFAULT_RETRY
            ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
            : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

        alert(
          `${errorEnum}`,
          `Error: ${errMsg} => Check BE Logs with customer Id`,
          PAGER_DUTY_ERROR_URGENCY.HIGH,
        );

        // Then check if we should retry
        if (currentRetryCount < DEFAULT_RETRY) {
          // Note: The reducer will have popped the action already
          // Only retry if we still have actions
          if (state.cartLoyaltyV2Reducer.cartActions.length > 0) {
            console.log(
              "Middleware retrying other error:",
              currentRetryCount + 1,
            );
            (store.dispatch as AppDispatch)(
              calculateCartWithOffersCall({
                retryCount: currentRetryCount + 1,
              }),
            );
          }
          // If no actions left, the reducer will reset the cart
        }
        // If max retries reached, the reducer will reset the cart
      }

      return result;
    }

    // Original middleware logic for regular cart actions
    const token = getCookie(COOKIE_ENUM.TOKEN);
    const state = store.getState() as RootState;

    if (cartActions.includes(action.type) && token) {
      if (
        action.type === "cartLoyaltyV2/redeemOfferAction" &&
        state.cartLoyaltyV2Reducer.redirectToItem &&
        state.cartLoyaltyV2Reducer.redirectToItem.itemId !== -1
      ) {
        return result;
      }
      (store.dispatch as AppDispatch)(calculateCartWithOffersCall({}));
    }

    return result;
  };
